import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Channel } from '../../types/index';
import logger from '../../utils/logger';
import { saveChannelsLocally } from '../../utils/localStorageUtils';

export interface PlaylistState {
  channels: Channel[];
  selectedChannels: string[];  // Array of channel IDs
  isLoading: boolean;
  error: string | null;
  errorTimeout: number | null;
  notificationType: 'success' | 'error' | null;
}

const initialState: PlaylistState = {
  channels: [],
  selectedChannels: [],
  isLoading: false,
  error: null,
  errorTimeout: null,
  notificationType: null,
};

const playlistSlice = createSlice({
  name: 'playlist',
  initialState,
  reducers: {
    setChannels: (state, action: PayloadAction<Channel[]>) => {
      state.channels = action.payload;
      // Persist to localStorage
      saveChannelsLocally(state.channels);
    },
    appendChannels: (state, action: PayloadAction<Channel[]>) => {
      // Create a map of existing channels using URL as key
      const existingChannels = new Map(state.channels.map((ch: Channel) => [ch.url, ch]));
      
      // Add new channels if they don't exist
      action.payload.forEach((channel: Channel) => {
        if (!existingChannels.has(channel.url)) {
          state.channels.push(channel);
        }
      });
      
      // Sort channels by name
      state.channels.sort((a: Channel, b: Channel) => a.name.localeCompare(b.name));
      
      // Persist to localStorage
      saveChannelsLocally(state.channels);
    },
    addChannel: (state, action: PayloadAction<Channel>) => {
      logger.debug('Adding channel:', action.payload);
      const exists = state.channels.some((ch: Channel) => ch.url === action.payload.url);
      logger.debug('Channel exists check:', { exists, url: action.payload.url });
      if (!exists) {
        state.channels.push({...action.payload, id: action.payload.url});
        // Sort channels by name
        state.channels.sort((a: Channel, b: Channel) => a.name.localeCompare(b.name));
        logger.debug('Channel added successfully');
        
        // Persist to localStorage
        saveChannelsLocally(state.channels);
      }
    },
    updateChannel: (state, action: PayloadAction<Channel>) => {
      const index = state.channels.findIndex(ch => ch.id === action.payload.id);
      if (index !== -1) {
        state.channels[index] = action.payload;
        // Persist to localStorage
        saveChannelsLocally(state.channels);
      }
    },
    removeChannel: (state, action: PayloadAction<string>) => {
      state.channels = state.channels.filter(channel => channel.id !== action.payload);
      // Persist to localStorage
      saveChannelsLocally(state.channels);
    },
    clearChannels: (state) => {
      state.channels = [];
      // Persist empty array to localStorage
      saveChannelsLocally([]);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      const prevState = state.isLoading;
      state.isLoading = action.payload;
      // console.log(`Playlist loading state changed from ${prevState} to ${action.payload}`);
    },
    setError: (state, action: PayloadAction<{ message: string | null; type: 'success' | 'error' | null }>) => {
      logger.debug('Setting error state:', { 
        message: action.payload.message, 
        type: action.payload.type,
        previousMessage: state.error,
        previousType: state.notificationType
      });
      state.error = action.payload.message;
      state.notificationType = action.payload.type;
    },
    clearErrorTimeout: (state) => {
      if (state.errorTimeout) {
        window.clearTimeout(state.errorTimeout);
        state.errorTimeout = null;
      }
    },
    setErrorTimeout: (state, action: PayloadAction<number>) => {
      state.errorTimeout = action.payload;
    },
    setSelectedChannels: (state, action: PayloadAction<string[]>) => {
      state.selectedChannels = action.payload;
    },
    toggleChannelSelection: (state, action: PayloadAction<string>) => {
      const channelId = action.payload;
      const index = state.selectedChannels.indexOf(channelId);
      if (index === -1) {
        state.selectedChannels.push(channelId);
      } else {
        state.selectedChannels.splice(index, 1);
      }
    },
    clearSelectedChannels: (state) => {
      state.selectedChannels = [];
    },
  },
});

export const {
  setChannels,
  appendChannels,
  addChannel,
  updateChannel,
  removeChannel,
  clearChannels,
  setLoading,
  setError,
  clearErrorTimeout,
  setErrorTimeout,
  setSelectedChannels,
  toggleChannelSelection,
  clearSelectedChannels,
} = playlistSlice.actions;

export default playlistSlice.reducer;
